/**
 * Betrachter für eine geteilte persönliche Datei (#598).
 *
 * Sitzt unter `/f/:token` innerhalb von `ProtectedRoute`, aber AUSSERHALB
 * beider `RequireApplication`-Gates. Der Grund ist hier ein anderer als bei
 * `/d/:documentId` (#516): Diese Seite leitet nirgendwohin weiter, sie zeigt
 * die Datei selbst. Ein Gate davor würde Empfänger abweisen, die zwar den Link
 * haben, aber weder Desktop noch die shell-freie Oberfläche zugewiesen bekamen
 * — und genau die sollen hier trotzdem hineinschauen können.
 *
 * Der Link gewährt EINSICHT, keine Kopie. Nichts wandert in das Drive des
 * Empfängers, und es wird auch keine Zeile in seinem Namen geschrieben. Zwei
 * Dinge folgen daraus, und sie sind der Grund für diese Bauform:
 *
 *   - Der Widerruf wirkt tatsächlich. Bei Kopien schlösse er nur die Tür hinter
 *     Dateien, die längst draußen sind.
 *   - Gezeigt wird immer die AKTUELLE Fassung. Eine Kopie liefe still
 *     auseinander: Der Besitzer bessert nach, der Empfänger arbeitet weiter mit
 *     dem alten Stand, ohne dass es jemand merkt.
 *
 * Deshalb wird der Inhalt bei jedem Aufruf frisch geholt und der Link
 * serverseitig jedes Mal neu geprüft. Ein offen gelassener Betrachter darf nach
 * einem Widerruf keine Bytes mehr bekommen.
 *
 * Gerendert wird über `FilePreviewContent` — dieselbe Komponente, die Drive 2.0
 * und das Vorschaufenster benutzen.
 *
 * Welche Darstellung ausgeliefert wird, entscheidet der SERVER: Word und
 * PowerPoint kommen als PDF an, alles andere so wie gespeichert. Deshalb steht
 * hier bewusst keine Formatlogik — und deshalb darf der Blob auch nicht auf den
 * MIME-Typ des ursprünglichen Dateinamens umetikettiert werden, für ein
 * umgewandeltes Dokument wäre das schlicht falsch.
 *
 * Genau daran ist die erste Fassung gescheitert: Sie schickte jede Datei durch
 * die Office-Vorschau, worauf eine `.md` mit 415 antwortete, obwohl sie im
 * Drive tadellos angezeigt wird.
 */
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { FileQuestion, Link2Off, Loader2 } from 'lucide-react'
import { cdbTqsApiService } from '@/services/cdb-tqs-api-service'
import type { SharedFileInfo } from '@/services/cdb-tqs-api-service'
import { CdbTqsApiError } from '@/services/cdb-tqs/http'
import { FilePreviewContent } from '@/components/shared/document-preview/FilePreviewContent'
import { formatFileSize } from '@/utils/audioRecordingUtils'
import { logError, logInfo } from '@/lib/logger'

type ViewerState =
  | { state: 'loading' }
  /** Metadaten und Inhalt liegen vor. */
  | { state: 'ready'; info: SharedFileInfo; fileUrl: string }
  /** Widerrufen, unbekannt, gelöscht oder fremder Client — bewusst ununterscheidbar. */
  | { state: 'invalid' }
  /** Das Format hat keine Vorschau (415). Der Link ist in Ordnung. */
  | { state: 'unsupported'; info: SharedFileInfo }
  /** Backend nicht erreichbar o. ä. — ausdrücklich NICHT als "ungültig" zeigen. */
  | { state: 'error' }

/**
 * 404 ist die einzige Absage, die der Server auf diesem Pfad kennt — er
 * verschmilzt alle Gründe dahinter. 400/422 gehören dazu: ein abgeschnittener
 * Link ist kein gültiger Token, und das als "Backend nicht erreichbar" zu
 * melden wäre schlicht falsch.
 */
function isInvalidLink(error: unknown): boolean {
  return (
    error instanceof CdbTqsApiError &&
    (error.status === 404 || error.status === 403 || error.status === 400 || error.status === 422)
  )
}

/** 415 heißt: der Link trägt, aber für dieses Format gibt es keine Vorschau. */
function isUnsupportedFormat(error: unknown): boolean {
  return error instanceof CdbTqsApiError && error.status === 415
}

export function SharedFilePage() {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()
  const [viewer, setViewer] = useState<ViewerState>({ state: 'loading' })

  // Der Blob-URL überlebt den Effekt und muss beim Verlassen freigegeben
  // werden — sonst hält die Seite die Bytes bis zum Neuladen des Tabs fest.
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!token) {
      setViewer({ state: 'invalid' })
      return
    }

    let active = true
    const controller = new AbortController()

    void (async () => {
      try {
        // Metadaten zuerst: Sie rahmen die Seite auch dann, wenn der Inhalt
        // gleich an einem nicht darstellbaren Format scheitert.
        const info = await cdbTqsApiService.getSharedFileInfo(token)
        if (!active) return

        try {
          const fileUrl = await cdbTqsApiService.getSharedFileContent(token, controller.signal)
          if (!active) {
            URL.revokeObjectURL(fileUrl)
            return
          }
          objectUrlRef.current = fileUrl
          setViewer({ state: 'ready', info, fileUrl })
        } catch (contentError) {
          if (!active) return
          if (isUnsupportedFormat(contentError)) {
            setViewer({ state: 'unsupported', info })
            return
          }
          throw contentError
        }
      } catch (error) {
        if (!active) return
        if (controller.signal.aborted) return
        if (isInvalidLink(error)) {
          logInfo({ context: 'SharedFilePage' }, 'Share link not viewable')
          setViewer({ state: 'invalid' })
          return
        }
        logError(error as Error, { context: 'SharedFilePage' })
        setViewer({ state: 'error' })
      }
    })()

    return () => {
      active = false
      controller.abort()
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [token])

  if (viewer.state === 'loading') {
    return <ViewerMessage icon="spinner" title={t('sharedFile.loading')} />
  }

  if (viewer.state === 'invalid') {
    return (
      <ViewerMessage
        icon="broken"
        title={t('sharedFile.invalid.title')}
        message={t('sharedFile.invalid.message')}
      />
    )
  }

  if (viewer.state === 'error') {
    return (
      <ViewerMessage
        icon="question"
        title={t('sharedFile.error.title')}
        message={t('sharedFile.error.message')}
      />
    )
  }

  const { info } = viewer

  return (
    <div className="app-surface flex h-full min-h-screen w-full flex-col">
      <header className="flex flex-col gap-1 border-b border-[var(--window-border-color)] px-6 py-4">
        <h1 className="truncate text-base font-medium text-[var(--text-color)]">
          {info.filename}
        </h1>
        <p className="text-sm text-[var(--text-secondary)]">
          {info.is_own_file
            ? t('sharedFile.header.own', { size: formatFileSize(info.size_bytes) })
            : info.shared_by_name
              ? t('sharedFile.header.sharedByNamed', {
                  name: info.shared_by_name,
                  size: formatFileSize(info.size_bytes),
                })
              : t('sharedFile.header.sharedBy', { size: formatFileSize(info.size_bytes) })}
        </p>
      </header>

      {viewer.state === 'unsupported' ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="max-w-md text-center text-sm text-[var(--text-secondary)]">
            {t('sharedFile.unsupported')}
          </p>
        </div>
      ) : (
        // `min-h-0` ist tragend: FilePreviewContent ist `flex-1 min-h-0` und
        // fällt in einem nicht begrenzten Elternteil auf Inhaltshöhe zusammen —
        // dann scrollt das PDF nicht mehr.
        <div className="flex min-h-0 flex-1 flex-col">
          <FilePreviewContent
            file={{
              name: info.filename,
              fileName: info.filename,
              fileUrl: viewer.fileUrl,
              fileSize: info.size_bytes,
            }}
          />
        </div>
      )}
    </div>
  )
}

function ViewerMessage({
  icon,
  title,
  message,
}: {
  icon: 'spinner' | 'broken' | 'question'
  title: string
  message?: string
}) {
  return (
    <div className="app-surface flex h-full min-h-screen w-full flex-col items-center justify-center gap-3 p-8 text-center">
      {icon === 'spinner' && (
        <Loader2 className="h-7 w-7 animate-spin motion-reduce:animate-none text-[var(--text-secondary)]" />
      )}
      {icon === 'broken' && <Link2Off className="h-7 w-7 text-[var(--text-secondary)]" />}
      {icon === 'question' && <FileQuestion className="h-7 w-7 text-[var(--text-secondary)]" />}
      <h1 className="text-lg font-medium text-[var(--text-color)]">{title}</h1>
      {message && <p className="max-w-md text-sm text-[var(--text-secondary)]">{message}</p>}
    </div>
  )
}
